import React, { useEffect, useState, useCallback } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { CrudSection } from '../components/CrudSection';
import { KpiCard } from '../components/KpiCard';
import { PageHeader, LoadingKpis, ErrorState } from '../components/States';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatCurrency, formatPercent, formatNumber, formatEurPerWp } from '../lib/format';
import { PieChart, Pie, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Factory, Globe, TrendingUp, SlidersHorizontal, Sun } from 'lucide-react';
import { toast } from 'sonner';

function ProdParamsCard({ assumptions, sid, canWrite, onSaved }) {
  const [cost, setCost] = useState(String(assumptions?.prod_cost_wp_ita ?? 0.24));
  const [mixCina, setMixCina] = useState(String(+(((assumptions?.prod_mix_cina ?? 0.78)) * 100).toFixed(2)));
  const [saving, setSaving] = useState(false);

  const costNum = parseFloat(String(cost).replace(',', '.')) || 0;
  const mixFraction = Math.min(Math.max((parseFloat(String(mixCina).replace(',', '.')) || 0) / 100, 0), 1);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateScenario(sid, { assumptions: { ...(assumptions || {}), prod_cost_wp_ita: costNum, industrial_cost_wp: costNum, prod_mix_cina: mixFraction } });
      toast.success('Parametri di produzione aggiornati', { description: `Costo ITA ${formatEurPerWp(costNum)} · Mix Cina ${formatPercent(mixFraction)}` });
      onSaved && onSaved();
    } catch (e) {
      toast.error('Errore nel salvataggio dei parametri');
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid="prod-params-card">
      <div className="flex items-center gap-2 mb-1"><SlidersHorizontal className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold text-foreground">Parametri di produzione</h3></div>
      <p className="text-xs text-muted-foreground mb-4">Il <strong>Costo ITA €/Wp</strong> è l'unico costo industriale considerato: base di calcolo del prezzo (e quindi del margine) dei pannelli FV nel Mix Installatori e del vantaggio di costo (Gap EU/Asia). La <strong>% Mix produttivo Cina</strong> definisce la quota prodotta in Asia.</p>
      <div className="grid grid-cols-12 gap-4 items-end">
        <div className="col-span-12 sm:col-span-4">
          <label className="text-xs text-muted-foreground">Costo ITA €/Wp (input principale)</label>
          <Input type="number" step="0.0001" value={cost} onChange={(e) => setCost(e.target.value)} disabled={!canWrite} className="mt-1" data-testid="prod-cost-ita-input" />
        </div>
        <div className="col-span-12 sm:col-span-4">
          <label className="text-xs text-muted-foreground">% Mix produttivo Cina</label>
          <div className="flex items-center gap-2 mt-1">
            <Input type="number" step="0.5" value={mixCina} onChange={(e) => setMixCina(e.target.value)} disabled={!canWrite} className="w-32" data-testid="prod-mix-cina-input" />
            <span className="text-sm text-muted-foreground">% Cina · {formatNumber(100 - (mixFraction * 100), 1)}% ITA</span>
          </div>
        </div>
        <div className="col-span-12 sm:col-span-4 flex justify-end">
          {canWrite && <Button onClick={save} disabled={saving} data-testid="prod-params-save">{saving ? 'Salvo…' : 'Applica'}</Button>}
        </div>
      </div>
    </Card>
  );
}

export default function ProduzioneEUAsia() {
  const { active, sid, loading, error, reload } = useScenario();
  const { canWriteList } = useAuth();
  const canWrite = canWriteList('china_imports');
  const [kpi, setKpi] = useState(null);

  const loadKpi = useCallback(() => { if (sid) api.getKpi(sid).then(setKpi).catch(() => {}); }, [sid]);
  useEffect(() => { loadKpi(); }, [loadKpi, active]);
  const reloadAll = useCallback(() => { reload(); loadKpi(); }, [reload, loadKpi]);

  if (loading || !kpi) return (<div><PageHeader title="Produzione EU/Asia" /><LoadingKpis count={4} /></div>);
  if (error || !active) return (<div><PageHeader title="Produzione EU/Asia" /><ErrorState onRetry={reload} /></div>);

  const rows = active.china_imports || [];
  const cards = [
    { label: 'Gap EU/Asia', value: formatCurrency(kpi.gap_eu_asia), sub: 'Vantaggio produzione Asia', tone: 'good', icon: Globe },
    { label: 'Volume totale', value: formatNumber(kpi.prod_volume_wp / 1000, 0) + ' kWp', sub: `${formatNumber(kpi.prod_volume_wp / 1000000, 2)} MWp`, icon: Factory },
    { label: 'Mix Asia / EU', value: `${formatPercent(kpi.prod_mix_cina)} / ${formatPercent(kpi.prod_mix_ita)}`, sub: 'Ripartizione produzione', icon: TrendingUp },
    { label: 'Costo medio ponderato', value: formatEurPerWp(kpi.prod_cost_weighted_wp), sub: `Asia ${formatEurPerWp(kpi.prod_cost_wp_cina)} · EU ${formatEurPerWp(kpi.prod_cost_wp_ita)}`, icon: Factory },
  ];
  const prodData = [
    { name: 'Asia', volume: kpi.prod_volume_cina_wp / 1000, cost: kpi.prod_cost_cina_eur },
    { name: 'EU/Italia', volume: kpi.prod_volume_ita_wp / 1000, cost: kpi.prod_cost_ita_eur },
  ];

  // Riepilogo pannelli FV nel mix di vendita (solo is_panel) e relativi prezzi di vendita
  const panels = (kpi.product_mix_details || []).filter((d) => d.is_panel);
  const panelRevTotal = panels.reduce((s, p) => s + (p.revenue_derived || 0), 0) || 1;
  const PANEL_COLORS = ['hsl(152,45%,32%)', 'hsl(205,78%,45%)', 'hsl(262,52%,55%)', 'hsl(38,92%,50%)', 'hsl(160,60%,42%)', 'hsl(0,72%,55%)'];

  // Origine produttiva dei pannelli in vendita (UE vs Extra UE) — sola lettura
  const itaMw = (kpi.panel_vol_ita_wp || 0) / 1e6;
  const extraMw = (kpi.panel_vol_extra_ue_wp || 0) / 1e6;
  const unassignedMw = (kpi.panel_vol_unassigned_wp || 0) / 1e6;
  const panelVolTotalMw = (kpi.panel_vol_total_wp || 0) / 1e6;
  const unassignedShare = kpi.panel_vol_total_wp ? (kpi.panel_vol_unassigned_wp || 0) / kpi.panel_vol_total_wp : 0;
  // Made in Italy combinato (pannelli Installatori + Impianti) — sola lettura
  const madeItalyMw = (kpi.made_italy_vol_wp || 0) / 1e6;
  const combinedTotalMw = (kpi.combined_vol_total_wp || 0) / 1e6;
  const impiantiMadeItalyMw = (kpi.impianti_made_italy_wp || 0) / 1e6;
  const impiantiTotalMw = (kpi.impianti_total_wp || 0) / 1e6;

  const columns = [
    { key: 'label', header: 'Importazione', render: (r) => <span className="font-medium">{r.label || '—'}</span> },
    { key: 'date', header: 'Data', render: (r) => r.date || '—' },
    { key: 'kwp', header: 'Volume kWp', align: 'right', render: (r) => formatNumber(r.kwp, 0) },
    { key: 'cost_per_wp', header: 'Costo €/Wp', align: 'right', render: (r) => formatEurPerWp(r.cost_per_wp) },
    { key: 'tot', header: 'Costo totale', align: 'right', render: (r) => formatCurrency((r.kwp || 0) * 1000 * (r.cost_per_wp || 0)), sortValue: (r) => (r.kwp || 0) * (r.cost_per_wp || 0) },
  ];
  const fields = [
    { name: 'label', label: 'Etichetta importazione', type: 'text' },
    { name: 'date', label: 'Data (ISO, es. 2026-02-15)', type: 'text' },
    { name: 'kwp', label: 'Volume (kWp)', type: 'number' },
    { name: 'cost_per_wp', label: 'Costo all-in (€/Wp)', type: 'number', step: '0.0001' },
    { name: 'note', label: 'Note', type: 'textarea' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Produzione EU/Asia" description="Composizione produttiva e vantaggio di costo (Gap EU/Asia)" />
      <div className="grid grid-cols-12 gap-4">{cards.map((c, i) => <div key={i} className="col-span-12 sm:col-span-6 xl:col-span-3"><KpiCard {...c} /></div>)}</div>
      <ProdParamsCard assumptions={active.assumptions} sid={sid} canWrite={canWrite} onSaved={reloadAll} />
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-1">Volume di produzione per origine (kWp)</h3>
        <p className="text-xs text-muted-foreground mb-4">Composizione percentuale del volume totale prodotto</p>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={prodData} dataKey="volume" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
              <Cell fill="hsl(38,92%,50%)" /><Cell fill="hsl(215,14%,52%)" />
            </Pie>
            <Tooltip formatter={(v) => formatNumber(v, 0) + ' kWp'} />
          </PieChart>
        </ResponsiveContainer>
        <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-1.5 text-xs">
          {prodData.map((e, i) => (
            <span key={i} className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: i === 0 ? 'hsl(38,92%,50%)' : 'hsl(215,14%,52%)' }} />
              <span className="text-muted-foreground">{e.name}</span>
              <span className="font-semibold tabular-nums text-foreground">{formatNumber(e.volume, 0)} kWp</span>
            </span>
          ))}
        </div>
      </Card>

      {/* Origine produttiva dei pannelli in vendita: % UE vs Extra UE + MW per stabilimento */}
      <Card className="p-5" data-testid="panel-origin-summary">
        <div className="flex items-center gap-2 mb-1"><Globe className="h-4 w-4 text-primary" /><h3 className="text-sm font-semibold text-foreground">Beneficio della vendita &middot; Made in Italy</h3><Badge variant="outline" className="text-[10px] font-normal">Sola lettura · dal Mix</Badge></div>
        <p className="text-xs text-muted-foreground mb-4">Quota di prodotto realizzato in Italia sul totale dei prodotti in vendita (pannelli Divisione Installatori + Divisione Impianti), espressa in % e in MW.</p>

        {/* Totale combinato Made in Italy */}
        <div className="rounded-lg border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-4 grid grid-cols-12 gap-4 items-center mb-4" data-testid="made-italy-total">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Prodotto in Italia (totale)</div>
            <div className="text-4xl font-semibold tabular-nums" style={{ color: 'hsl(var(--success))' }} data-testid="made-italy-share">{formatPercent(kpi.made_italy_share_total, 1)}</div>
            <div className="text-xs text-muted-foreground mt-1">{formatNumber(madeItalyMw, 2)} MW su {formatNumber(combinedTotalMw, 2)} MW totali</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="h-3 w-full rounded-full overflow-hidden bg-muted flex" role="img" aria-label="Quota Made in Italy sul totale">
              <div style={{ width: `${(kpi.made_italy_share_total || 0) * 100}%`, background: 'hsl(var(--success))' }} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Somma dei pannelli prodotti in Italia (Installatori) e della quota Made in Italy delle categorie Impianti.</p>
          </div>
        </div>

        {/* Dettaglio per divisione */}
        <div className="grid grid-cols-12 gap-4">
          {/* Installatori: pannelli per stabilimento */}
          <div className="col-span-12 lg:col-span-6 rounded-lg border p-4" data-testid="origin-installatori">
            <div className="text-xs font-semibold text-foreground mb-2">Divisione Installatori · pannelli FV</div>
            {panelVolTotalMw <= 0 ? (
              <p className="text-sm text-muted-foreground">Nessun volume pannelli disponibile.</p>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-2">Prodotto in UE <span className="font-semibold text-[hsl(var(--success))]" data-testid="panel-eu-share">{formatPercent(kpi.panel_share_eu, 1)}</span> · Extra UE {formatPercent(kpi.panel_share_extra_ue, 1)}{unassignedShare > 0.0005 ? ` · Da assegnare ${formatPercent(unassignedShare, 1)}` : ''}</div>
                <div className="h-2.5 w-full rounded-full overflow-hidden bg-muted flex mb-3">
                  <div style={{ width: `${(kpi.panel_share_eu || 0) * 100}%`, background: 'hsl(var(--success))' }} />
                  <div style={{ width: `${(kpi.panel_share_extra_ue || 0) * 100}%`, background: 'hsl(215,14%,52%)' }} />
                  <div style={{ width: `${unassignedShare * 100}%`, background: 'hsl(var(--warning))' }} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div data-testid="panel-mw-ita"><div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: 'hsl(var(--success))' }} />Italia (UE)</div><div className="text-sm font-semibold tabular-nums">{formatNumber(itaMw, 2)} MW</div></div>
                  <div data-testid="panel-mw-extra"><div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: 'hsl(215,14%,52%)' }} />Extra UE</div><div className="text-sm font-semibold tabular-nums">{formatNumber(extraMw, 2)} MW</div></div>
                  {unassignedMw > 0.0005 && <div data-testid="panel-mw-unassigned"><div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: 'hsl(var(--warning))' }} />Da assegnare</div><div className="text-sm font-semibold tabular-nums">{formatNumber(unassignedMw, 2)} MW</div></div>}
                </div>
                {unassignedMw > 0.0005 && <p className="text-[11px] text-[hsl(var(--warning))] mt-2">Alcuni pannelli non hanno ancora lo stabilimento assegnato (impostalo nel Mix Installatori).</p>}
              </>
            )}
          </div>
          {/* Impianti: Made in Italy */}
          <div className="col-span-12 lg:col-span-6 rounded-lg border p-4" data-testid="origin-impianti">
            <div className="text-xs font-semibold text-foreground mb-2">Divisione Impianti · Made in Italy</div>
            {impiantiTotalMw <= 0 ? (
              <p className="text-sm text-muted-foreground">Nessun volume Impianti disponibile.</p>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-2">Quota Made in Italy <span className="font-semibold text-[hsl(var(--success))]" data-testid="impianti-mi-share">{formatPercent(impiantiTotalMw ? impiantiMadeItalyMw / impiantiTotalMw : 0, 1)}</span> del volume Impianti</div>
                <div className="h-2.5 w-full rounded-full overflow-hidden bg-muted flex mb-3">
                  <div style={{ width: `${impiantiTotalMw ? (impiantiMadeItalyMw / impiantiTotalMw) * 100 : 0}%`, background: 'hsl(var(--success))' }} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div data-testid="impianti-mw-italy"><div className="text-[11px] text-muted-foreground flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: 'hsl(var(--success))' }} />Made in Italy</div><div className="text-sm font-semibold tabular-nums">{formatNumber(impiantiMadeItalyMw, 2)} MW</div></div>
                  <div data-testid="impianti-mw-total"><div className="text-[11px] text-muted-foreground">Volume Impianti</div><div className="text-sm font-semibold tabular-nums">{formatNumber(impiantiTotalMw, 2)} MW</div></div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Imposta il flag "Made in Italy" e la relativa % nelle categorie del Mix Impianti.</p>
              </>
            )}
          </div>
        </div>
      </Card>
      <div>
        <div className="mb-3"><h3 className="text-sm font-semibold">Importazioni dalla Cina</h3><p className="text-xs text-muted-foreground">Se presenti, determinano il costo medio ponderato €/Wp ({kpi.china_imports_source === 'importazioni' ? 'da importazioni' : 'valore manuale'})</p></div>
        <CrudSection rows={rows} columns={columns} fields={fields} canWrite={canWrite} addLabel="Nuova importazione"
          onAdd={(p) => api.addChinaImport(sid, p)} onUpdate={(id, p) => api.updateChinaImport(sid, id, p)} onDelete={(id) => api.deleteChinaImport(sid, id)} afterChange={reloadAll} describeRow={(r) => `Eliminare "${r.label}"?`} />
      </div>
    </div>
  );
}
